import React, { useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { Autocomplete, Button, IconButton, Stack, TextField } from "@mui/material";
import { AutocompleteMultiSelect } from "components/reports/AutocompleteMultiSelect/AutocompleteMultiSelect";
import { useMeasures } from "hooks";
import { startCase } from "lodash";
import { useReportsMetaSelector } from "store/selectors/reports-selectors";
import { IAxis } from "store/types";
import { camelCaseToTitle, normalizeGroupByCharacteristic } from "utils";

import { chartTypesCollection } from "../../../constants";
import { State } from "./CreateReportSection";

interface Props {
  data: State["graph"];
  setData: (cb: (prevState: State["graph"]) => State["graph"]) => void;
}

export const Graph: React.FC<Props> = ({ data, setData }) => {
  const chartTypes = Object.keys(chartTypesCollection);
  const measures = useMeasures(false)
    .filter((item) => !(typeof item === "object" && item.hasOwnProperty("role"))) // repeat logic from reports AutocompleteMultiSelect
    .map((item) => (typeof item === "string" ? item : String(item.value)));
  const { characteristics } = useReportsMetaSelector();

  const groupByOptions: string[] = useMemo(() => {
    return ["investor", "deal", ...characteristics.map((char) => normalizeGroupByCharacteristic(char))];
  }, [characteristics]);

  const handleAdd = () => {
    setData((prevState) => {
      const maxId = prevState.reduce<number>((acc, item) => {
        const currentItemId = Number(item.id);
        return currentItemId > acc ? currentItemId : acc;
      }, 0);

      return [
        ...prevState,
        { id: String(maxId + 1), chart_type: null, group_by: null, measure: null, isDeleted: false }
      ];
    });
  };

  const handleChange = ({
    id,
    value,
    key
  }: {
    id: number | string;
    value: string | null | IAxis;
    key: keyof State["graph"][number];
  }) => {
    setData((prevState) => {
      // return prevState.map((item) => (item.id === id ? { ...item, [key]: value } : item));
      return prevState.map((item) => {
        if (item.id !== id) return item;

        const result = { ...item, [key]: value };

        // clear axis if chart type switched from scatter to other
        if (item.chart_type === "scatter" && key === "chart_type" && value !== "scatter") {
          delete result.xAxis;
          delete result.yAxis;
        }

        return result;
      });
    });
  };

  const handleDelete = ({ id }: { id: number | string }) => {
    if (typeof id === "string") {
      setData((prevState) => prevState.filter((item) => item.id !== id));
    } else {
      setData((prevState) => prevState.map((item) => (item.id === id ? { ...item, isDeleted: true } : item)));
    }
  };

  const handleClearScatterAxis = ({ id, key }: { id: number | string; key: "xAxis" | "yAxis" }) => {
    setData((prevState) => {
      return prevState.map((item) => {
        if (item.id === id) {
          const itemCopy = { ...item };
          delete itemCopy[key];
          return itemCopy;
        }
        return item;
      });
    });
  };

  const handleUndoDelete = ({ id }: { id: number | string }) => {
    setData((prevState) => prevState.map((item) => (item.id === id ? { ...item, isDeleted: false } : item)));
  };

  return (
    <Stack sx={{ gap: "14px", width: 600 }}>
      {data.map((item) => (
        <Stack key={item.id} sx={{ gap: "10px", flexDirection: "row" }}>
          <Stack sx={{ flexGrow: 1, gap: "10px" }}>
            <Stack sx={{ gap: "10px", flexDirection: "row", alignItems: "center", flexGrow: 1 }}>
              <Autocomplete
                fullWidth
                options={chartTypes}
                disabled={item.isDeleted}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Chart Type"
                    {...(item.isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
                  />
                )}
                value={item.chart_type}
                getOptionLabel={(option) => camelCaseToTitle(option)}
                renderOption={(props, option) => <li {...props}>{startCase(option)}</li>}
                onChange={(_, value) => handleChange({ id: item.id, value, key: "chart_type" })}
              />
              {item.chart_type !== "scatter" && (
                <AutocompleteMultiSelect
                  fullWidth
                  sx={{
                    ".MuiFormControl-root": {
                      ".MuiOutlinedInput-root": { padding: "6px !important" }
                    }
                  }}
                  defaultValue={item.measure}
                  getOptionLabel={(option) => {
                    if ((option as string).startsWith("calc-field-")) {
                      return (option as string).substring("calc-field-".length);
                    }
                    return camelCaseToTitle(option as string);
                  }}
                  label="Value"
                  options={measures}
                  renderOption={(props, option) => <li {...props}>{startCase(option as string)}</li>}
                  onChange={(value) => {
                    handleChange({ id: item.id, value: value as string, key: "measure" });
                  }}
                />
              )}
              {/* {item.chart_type !== "scatter" && (
                <Autocomplete
                  fullWidth
                  options={measures}
                  disabled={item.isDeleted}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Value"
                      {...(item.isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
                    />
                  )}
                  value={item.measure}
                  getOptionLabel={(option) => {
                    if (option.startsWith("calc-field-")) {
                      return option.substring("calc-field-".length);
                    }
                    return camelCaseToTitle(option);
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.startsWith("calc-field-")
                        ? option.substring("calc-field-".length)
                        : camelCaseToTitle(option)}
                    </li>
                  )}
                  onChange={(_, value) => handleChange({ id: item.id, value, key: "measure" })}
                />
              )} */}
              <Autocomplete
                fullWidth
                options={groupByOptions}
                disabled={item.isDeleted}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="By"
                    {...(item.isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
                  />
                )}
                value={item.group_by}
                getOptionLabel={(option) => camelCaseToTitle(option)}
                renderOption={(props, option) => <li {...props}>{startCase(option)}</li>}
                onChange={(_, value) => handleChange({ id: item.id, value, key: "group_by" })}
              />
            </Stack>
            {item.chart_type === "scatter" && (
              <Stack sx={{ flexDirection: "row", gap: "10px" }}>
                <Autocomplete
                  fullWidth
                  options={measures}
                  disabled={item.isDeleted}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="xAxis"
                      {...(item.isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
                    />
                  )}
                  value={item.xAxis?.name || null}
                  getOptionLabel={(option) => camelCaseToTitle(option)}
                  renderOption={(props, option) => <li {...props}>{camelCaseToTitle(option)}</li>}
                  onChange={(_, value) => {
                    if (!value) {
                      return handleClearScatterAxis({ id: item.id, key: "xAxis" });
                    }
                    handleChange({
                      id: item.id,
                      value: { name: value, type: "measure" },
                      key: "xAxis"
                    });
                  }}
                />
                <Autocomplete
                  fullWidth
                  options={measures}
                  disabled={item.isDeleted}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="yAxis"
                      {...(item.isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
                    />
                  )}
                  value={item.yAxis?.name || null}
                  getOptionLabel={(option) => camelCaseToTitle(option)}
                  renderOption={(props, option) => <li {...props}>{camelCaseToTitle(option)}</li>}
                  onChange={(_, value) => {
                    if (!value) {
                      return handleClearScatterAxis({ id: item.id, key: "yAxis" });
                    }
                    handleChange({
                      id: item.id,
                      value: { name: value, type: "measure" },
                      key: "yAxis"
                    });
                  }}
                />
              </Stack>
            )}
          </Stack>
          {item.isDeleted ? (
            <IconButton onClick={() => handleUndoDelete({ id: item.id })}>
              <UndoIcon />
            </IconButton>
          ) : (
            <IconButton disabled={data.length < 2} onClick={() => handleDelete({ id: item.id })}>
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      ))}
      <Button onClick={handleAdd} variant="text" sx={{ alignSelf: "flex-start" }} startIcon={<AddIcon />}>
        Add Graph
      </Button>
    </Stack>
  );
};
